import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import type {
  PortalBrandingResponse,
  PortalClientFile,
  PortalLinkViewResponse,
  PortalSessionResponse,
  PortalSigninLinkFailure,
} from "@ledgerbox/domain"

import { errorMessage, PortalApiError, portalApi, type PortalApi } from "./api"
import { PortalApiProvider, usePortalApi } from "./api-context"
import { ChecklistView } from "./checklist"
import { EngagementWorkbench } from "./engagement-workbench"
import { HistoryView } from "./history"
import { SignIn } from "./sign-in"
import { LinkView } from "./link-view"
import { PortalThreads } from "./portal-threads"
import { PortalDeliverables } from "./portal-deliverables"
import { PortalHelp } from "./portal-help"
import { usePortalAttention } from "./attention"
import { isSentBack, itemsInPageOrder } from "./item-state"
import { ItemsToFixButton, type ItemToFix } from "./items-to-fix"
import { readLinkValue, revealRequestAnchor, takeSigninToken } from "./link"
import { ConfirmDialog, UploadAnnouncer } from "./ui"
import { signedInName, signinLinkFailureOf } from "./sign-in-model"
import {
  AccountMenu,
  LoadingScreen,
  MissingLink,
  PortalNav,
  PortalShell,
  PortalTabs,
  sectionLabels,
  UnknownPortal,
  type PortalView,
} from "./portal-shell"
import { useUploadQueue } from "./use-upload-queue"

/**
 * The portal, over whichever conversation it is given.
 *
 * `api` defaults to the live one. The demo passes its own, and everything
 * below this line is the shipping portal either way: same CSS, same nav, same
 * breakpoints, same interactions. See ./demo.
 */
export function PortalApp({
  api = portalApi,
  branding,
  view,
}: {
  api?: PortalApi
  /** The brand to wear instead of the api's. The firm app's preview sends a
      new one on every edit, and the portal redraws in place. */
  branding?: PortalBrandingResponse
  /** The section to show, for the firm app's preview. A new value moves
      there; the client can still move on from it. */
  view?: PortalView
}) {
  return (
    <PortalApiProvider api={api}>
      <PortalRoot brandingOverride={branding ?? null} viewOverride={view} />
    </PortalApiProvider>
  )
}

function PortalRoot({
  brandingOverride,
  viewOverride,
}: {
  brandingOverride: PortalBrandingResponse | null
  viewOverride?: PortalView
}) {
  const api = usePortalApi()
  const linkToken = readLinkValue()
  const [fetchedBranding, setBranding] =
    useState<PortalBrandingResponse | null>(null)
  const [fetchedSession, setSession] = useState<PortalSessionResponse | null>(
    null
  )
  const branding = brandingOverride ?? fetchedBranding
  // The session carries the brand too (the home greeting reads it there), so
  // a live brand replaces it in both places.
  const session = useMemo(
    () =>
      fetchedSession && brandingOverride
        ? { ...fetchedSession, branding: brandingOverride }
        : fetchedSession,
    [fetchedSession, brandingOverride]
  )
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<PortalView>(viewOverride ?? "checklist")
  useEffect(() => {
    if (viewOverride) setView(viewOverride)
  }, [viewOverride])
  const [itemToFix, setItemToFix] = useState<ItemToFix | null>(null)
  const [logoutError, setLogoutError] = useState<string | null>(null)
  const [switchNotice, setSwitchNotice] = useState<string | null>(null)
  // The token from a request email leaves the address bar before the first
  // request goes out. One exchange per page load, even when strict mode runs
  // the effect twice: a second POST would find the link already used.
  const [signinToken] = useState(takeSigninToken)
  const [linkFailure, setLinkFailure] =
    useState<PortalSigninLinkFailure | null>(null)
  // Without a session, a client link can still send: the write-only view.
  const [linkView, setLinkView] = useState<PortalLinkViewResponse | null>(
    null
  )
  const [wantsSignIn, setWantsSignIn] = useState(false)
  const opening = useRef<Promise<PortalSessionResponse> | null>(null)

  useEffect(() => {
    let active = true
    opening.current ??=
      signinToken && linkToken
        ? api.exchangeSigninLink({ linkToken, signinToken })
        : api.getSession()
    Promise.allSettled([api.getBranding(), opening.current]).then(
      async ([brandingResult, sessionResult]) => {
        if (!active) return
        if (brandingResult.status === "fulfilled") {
          setBranding(brandingResult.value)
          document.title = `${brandingResult.value.displayName} client portal`
        }
        // No session for this client on a plain visit to the link: the link
        // alone can send files and answers, unless the firm turned that off.
        // A browser signed in to another client gets the same, so sending
        // does not wait on switching.
        const openLinkView =
          sessionResult.status === "rejected" &&
          sessionResult.reason instanceof PortalApiError &&
          (sessionResult.reason.status === 401 ||
            sessionResult.reason.code === "switch_required") &&
          linkToken &&
          !signinToken
        if (openLinkView) {
          const view = await api.openLink().catch(() => null)
          if (!active) return
          if (view) setLinkView(view)
        }
        if (sessionResult.status === "fulfilled") {
          setSession(sessionResult.value)
        } else if (
          sessionResult.reason instanceof PortalApiError &&
          sessionResult.reason.code === "switch_required"
        ) {
          setSwitchNotice(
            "You are signed in to another client. Verify your email to open this client’s portal."
          )
        } else if (sessionResult.reason instanceof PortalApiError) {
          setLinkFailure(signinLinkFailureOf(sessionResult.reason.details))
        }
        setLoading(false)
      }
    )
    return () => {
      active = false
    }
  }, [api, linkToken, signinToken])

  // The request the email named, once the list it sits in has drawn.
  const signedIn = Boolean(session)
  useEffect(() => {
    if (signedIn) revealRequestAnchor()
  }, [signedIn])

  // Called before the early returns below: hooks run in the same order on
  // every render.
  const attention = usePortalAttention(Boolean(session), view)
  const counts = { messages: attention.messages, foryou: attention.foryou }

  if (loading) return <LoadingScreen />
  // No branding means the link names no firm, so there is no portal to open.
  if (!branding) return <UnknownPortal />

  // The same test ClientPortal uses to pick the engagement task list.
  const engagement = Boolean(
    session?.requests.some((request) => request.engagementId)
  )
  const pageItems = session ? itemsInPageOrder(session.requests, engagement) : []

  return (
    <PortalShell
      branding={branding}
      clientName={
        session
          ? (session.workspace.clientCompanyName ?? session.workspace.name)
          : linkView && !wantsSignIn
            ? (linkView.workspace.clientCompanyName ?? linkView.workspace.name)
            : null
      }
      nav={
        session ? (
          <PortalNav
            view={view}
            counts={counts}
            onChange={setView}
          />
        ) : null
      }
      tabs={
        session ? (
          <PortalTabs
            view={view}
            counts={counts}
            onChange={setView}
          />
        ) : null
      }
      help={
        session ? (
          <PortalHelp
            branding={branding}
            sectionLabel={sectionLabels.checklist}
            mode={engagement ? "engagement" : "checklist"}
          />
        ) : null
      }
      // Passed only while something is sent back: on a phone the shell draws
      // its own line for it, and an element that renders nothing still drew
      // an empty bordered strip there (R3-U5).
      toFix={
        session && pageItems.some(isSentBack) ? (
          <ItemsToFixButton
            items={pageItems}
            onGo={(itemId) => {
              // From any section: the item lives on the task list.
              setView("checklist")
              setItemToFix({ itemId })
            }}
          />
        ) : null
      }
      signedInAs={session ? signedInName(session.contact) : null}
      account={
        session ? (
          <AccountMenu
            name={signedInName(session.contact)}
            onSignOut={() => {
              setLogoutError(null)
              void api
                .logout()
                .then(() => setSession(null))
                .catch((error) =>
                  setLogoutError(
                    errorMessage(
                      error instanceof Error
                        ? error
                        : new Error("Something went wrong. Try again.")
                    )
                  )
                )
            }}
          />
        ) : null
      }
    >
      {logoutError ? (
        <div role="alert" className="portal-account-error">
          {logoutError}
        </div>
      ) : null}
      {session ? (
        <ClientPortal
          session={session}
          firmName={branding.displayName}
          view={view}
          onView={setView}
          onSessionChange={setSession}
          itemToFix={itemToFix}
          onLanded={() => setItemToFix(null)}
        />
      ) : linkToken && linkView && !wantsSignIn ? (
        <LinkView
          view={linkView}
          firmName={branding.displayName}
          onViewChange={setLinkView}
          onSignIn={() => setWantsSignIn(true)}
        />
      ) : linkToken ? (
        <SignIn
          branding={branding}
          linkToken={linkToken}
          notice={switchNotice}
          signinToken={signinToken}
          linkFailure={linkFailure}
          onBack={linkView ? () => setWantsSignIn(false) : undefined}
          onSignedIn={(next) => {
            setSession(next)
            setLinkView(null)
            setWantsSignIn(false)
            setSwitchNotice(null)
            setLinkFailure(null)
            setView("checklist")
          }}
        />
      ) : (
        <MissingLink />
      )}
    </PortalShell>
  )
}

/* The upload engine sits above the sections so a client can move to Messages
   while a phone photo is still on its way. */
function ClientPortal({
  session,
  firmName,
  view,
  onView,
  onSessionChange,
  itemToFix,
  onLanded,
}: {
  session: PortalSessionResponse
  firmName: string
  view: PortalView
  onView(next: PortalView): void
  onSessionChange: (session: PortalSessionResponse) => void
  itemToFix: ItemToFix | null
  onLanded(): void
}) {
  const api = usePortalApi()
  const [actionError, setActionError] = useState<string | null>(null)

  const refresh = async () => onSessionChange(await api.getSession())
  const {
    uploads,
    queueNote,
    announcement,
    uploadOne,
    handleFiles,
    dismiss: dismissUpload,
  } = useUploadQueue(
    { mint: api.mintUpload, confirm: api.confirmUpload },
    refresh
  )

  // The file waiting on "Remove", asked about in the portal's own dialog.
  const [removing, setRemoving] = useState<PortalClientFile | null>(null)
  const removeFile = async (file: PortalClientFile) => {
    setRemoving(null)
    setActionError(null)
    try {
      onSessionChange(await api.deleteFile(file.fileId))
    } catch (error) {
      setActionError(
        errorMessage(
          error instanceof Error
            ? error
            : new Error("Something went wrong. Try again.")
        )
      )
    }
  }

  const clientName =
    session.workspace.clientCompanyName ?? session.workspace.name

  // Every section keeps the upload's live line, so a file that lands while
  // the client reads Messages is still heard.
  const withAnnouncer = (page: ReactNode) => (
    <>
      <UploadAnnouncer text={announcement} />
      {page}
    </>
  )

  if (view === "messages") {
    return withAnnouncer(
      <div className="page">
        <PortalThreads
          files={session.files}
          requests={session.requests}
          firmName={firmName}
          clientName={clientName}
        />
      </div>
    )
  }
  if (view === "foryou") {
    return withAnnouncer(
      <div className="page">
        <PortalDeliverables
          firmName={firmName}
          contactName={signedInName(session.contact)}
        />
      </div>
    )
  }
  if (view === "history") {
    return withAnnouncer(
      <>
        <HistoryView
          session={session}
          firmName={firmName}
          onRemoveFile={setRemoving}
          onBack={() => onView("checklist")}
          backLabel={sectionLabels.checklist}
          actionError={actionError}
        />
        <ConfirmDialog
          open={removing !== null}
          title={`Remove ${removing?.fileName ?? "this file"}?`}
          body={`${firmName} will no longer have this file`}
          confirmLabel="Remove"
          onConfirm={() => {
            if (removing) void removeFile(removing)
          }}
          onCancel={() => setRemoving(null)}
        />
      </>
    )
  }

  if (session.requests.some((request) => request.engagementId)) {
    return withAnnouncer(
      <EngagementWorkbench
        session={session}
        firmName={firmName}
        uploads={uploads}
        queueNote={queueNote}
        actionError={actionError}
        onActionError={setActionError}
        onFiles={handleFiles}
        onRetry={uploadOne}
        onDismissUpload={dismissUpload}
        onSessionChange={onSessionChange}
        onOpenMessages={() => onView("messages")}
        onOpenDeliverables={() => onView("foryou")}
        onOpenHistory={() => onView("history")}
        itemToFix={itemToFix}
        onLanded={onLanded}
      />
    )
  }
  return withAnnouncer(
    <ChecklistView
      session={session}
      firmName={firmName}
      uploads={uploads}
      queueNote={queueNote}
      actionError={actionError}
      onActionError={setActionError}
      onFiles={handleFiles}
      onRetry={uploadOne}
      onDismissUpload={dismissUpload}
      onSessionChange={onSessionChange}
      onOpenDeliverables={() => onView("foryou")}
      onOpenHistory={() => onView("history")}
      itemToFix={itemToFix}
      onLanded={onLanded}
    />
  )
}
